import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Database, Loader2, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { ConnectSourceDialog } from "@/components/ConnectSourceDialog";
import { UploadDataDialog } from "@/components/UploadDataDialog";
import { ConnectionProgressDrawer } from "@/components/ConnectionProgressDrawer";
import { SensitivityBadge } from "@/components/GlossaryBadges";
import { SCHEMA } from "@/constants/testIds";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

function groupBySchema(tables) {
  const groups = {};
  for (const t of tables) {
    (groups[t.schema_name] = groups[t.schema_name] || []).push(t);
  }
  return groups;
}

export default function Schema() {
  const { active } = useWorkspace();
  const [tables, setTables] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const load = useCallback(async () => {
    if (!active) return;
    const { data } = await api.get("/schema", { params: { workspace_id: active.id } });
    setTables(data);
  }, [active]);

  useEffect(() => {
    load();
  }, [load]);

  const onSeed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/sources/seed-sample", null, {
        params: { workspace_id: active.id },
      });
      toast.success(`Connected sample source · ${data.tables} tables`);
      setJobId(data.job_id);
      setDrawerOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Failed to seed");
      setSeeding(false);
    }
  };

  const onPipelineDone = async (job) => {
    setSeeding(false);
    if (job?.status === "completed") {
      await load();
      toast.success("Glossary generated");
    } else {
      toast.error(job?.error || "Connection failed");
    }
  };

  const groups = tables ? groupBySchema(tables) : {};

  return (
    <div data-testid={SCHEMA.root} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <ConnectionProgressDrawer
        jobId={jobId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onDone={onPipelineDone}
        title="Connecting sample data"
      />
      <main className="mx-auto w-full max-w-7xl px-6 py-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Schema</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Every table and column, with its semantic definition and sensitivity.
            </p>
          </div>
          {tables && tables.length > 0 && (
            <div className="flex items-center gap-2">
              <UploadDataDialog workspaceId={active?.id} />
              <ConnectSourceDialog onConnected={load} workspaceId={active?.id} />
              <Button
                variant="outline"
                onClick={onSeed}
                disabled={seeding}
                className="rounded-full border-border/80 text-xs uppercase tracking-[0.16em]"
              >
                {seeding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Database className="mr-2 h-4 w-4" />}
                Re-seed sample
              </Button>
            </div>
          )}
        </div>

        {tables === null && (
          <div className="mt-16 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-accent" />
          </div>
        )}

        {tables && tables.length === 0 && (
          <div
            data-testid={SCHEMA.empty}
            className="mt-12 flex flex-col items-center rounded-2xl border border-dashed border-border/60 bg-card/40 px-6 py-16 text-center"
          >
            <Database className="h-8 w-8 text-accent" />
            <h2 className="mt-5 text-lg font-semibold">No source connected</h2>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">
              Connect a sample senior-living community to introspect its schema and
              auto-generate an audit-grade glossary.
            </p>
            <Button
              onClick={onSeed}
              disabled={seeding}
              data-testid={SCHEMA.seedButton}
              className="mt-7 rounded-full bg-accent text-xs uppercase tracking-[0.18em] text-accent-foreground hover:bg-accent/90"
            >
              {seeding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Connect sample data
            </Button>
            <div className="mt-4 flex items-center gap-3 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              <span className="h-px w-8 bg-border" />
              or
              <span className="h-px w-8 bg-border" />
            </div>
            <div className="mt-4 flex items-center gap-2">
              <UploadDataDialog workspaceId={active?.id}>
                <Button
                  variant="outline"
                  data-testid="schema-upload-button"
                  className="rounded-full border-border/80 text-xs uppercase tracking-[0.16em]"
                >
                  <Database className="mr-2 h-4 w-4" />
                  Upload CSV / Parquet
                </Button>
              </UploadDataDialog>
              <ConnectSourceDialog
                onConnected={load}
                workspaceId={active?.id}
                trigger={
                  <Button
                    variant="outline"
                    data-testid={SCHEMA.pgButton}
                    disabled={seeding}
                    className="rounded-full border-border/80 text-xs uppercase tracking-[0.16em]"
                  >
                    <Database className="mr-2 h-4 w-4" />
                    Connect a database
                  </Button>
                }
              />
            </div>
          </div>
        )}

        {tables && tables.length > 0 && (
          <div className="mt-8 space-y-10">
            {Object.entries(groups).map(([schema, schemaTables]) => (
              <section key={schema}>
                <h2 className="mb-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">
                  {schema} · {schemaTables.length} tables
                </h2>
                <Accordion type="multiple" className="space-y-3">
                  {schemaTables.map((t) => (
                    <AccordionItem
                      key={t.id}
                      value={t.id}
                      data-testid={SCHEMA.table}
                      className="rounded-xl border border-border/60 bg-card/40 px-4"
                    >
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex flex-1 flex-col items-start gap-1.5 pr-4 text-left">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold">{t.table_fqn}</span>
                            <SensitivityBadge value={t.glossary?.sensitivity} />
                            {t.row_count != null && (
                              <span className="text-[10px] text-muted-foreground">
                                {t.row_count.toLocaleString()} rows
                              </span>
                            )}
                          </div>
                          <span className="text-xs font-normal text-muted-foreground">
                            {t.glossary?.definition || (
                              <Link
                                to="/glossary"
                                data-testid={SCHEMA.addDefinition}
                                className="text-accent hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                + Add definition
                              </Link>
                            )}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-1 border-t border-border/40 pt-3">
                          {t.columns.map((c) => (
                            <div
                              key={c.name}
                              className="grid grid-cols-12 items-start gap-2 rounded-md px-2 py-2 text-xs hover:bg-muted/30"
                            >
                              <div className="col-span-3 flex items-center gap-2">
                                <span className="font-medium">{c.name}</span>
                              </div>
                              <div className="col-span-2 text-muted-foreground">{c.data_type}</div>
                              <div className="col-span-5 text-muted-foreground">
                                {c.glossary?.definition || (
                                  <Link to="/glossary" className="text-accent hover:underline">
                                    + Add definition
                                  </Link>
                                )}
                              </div>
                              <div className="col-span-2 flex justify-end">
                                <SensitivityBadge value={c.glossary?.sensitivity} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            ))}
            <Link
              to="/glossary"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-accent hover:underline"
            >
              Open glossary
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
